import { type ReactNode } from 'react';
import { cn } from '@/utils/tailwind.util';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { type AiBuilderChoiceOption } from '../stores';
const AI_BUILDER_CHOICE_CHIP_CLASS_NAME = 'group flex min-h-10 min-w-0 max-w-[416px] justify-start gap-3xs overflow-hidden rounded-md border border-opacity-assistive bg-neutral px-sm py-xs text-default transition-[background-color,border-color,color,margin] duration-150 ease-out hover:mr-3xs hover:bg-alternative focus-visible:bg-alternative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-strong focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-neutral disabled:text-disabled';

/**
 * single/multiple choice option button의 공통 shell.
 * 선택 인디케이터(radio/checkbox)는 컴포지션으로 주입받는다.
 *
 * checkbox 인디케이터가 zus의 Radix 기반 Checkbox(button role=checkbox)를
 * 렌더링하므로, shell 자체는 button-in-button 중첩을 피하기 위해
 * role="button"의 div로 렌더링한다.
 *
 * 성취기준 리치 카드(2026-07-16): value(성취기준 코드)와 description 을 함께
 * 가진 옵션은 main text=`[value]`, sub text=label(본문) 2단으로 렌더한다
 * (인디케이터 상단 정렬). description 은 회신·매칭용 데이터로만 유지하고
 * 렌더하지 않는다. 그 외 옵션(quiz_types 등 value 만 있거나 라벨뿐인 경우)은
 * 기존 1줄 칩.
 */
export const AiBuilderChoiceOptionShell = ({
  indicator,
  onSelect,
  option,
  selected
}: {
  indicator: ReactNode;
  onSelect: () => void;
  option: AiBuilderChoiceOption;
  selected: boolean;
}) => {
  const copy = useAiBuilderChatCopy();
  const isRichCard = Boolean(option.value && option.description);
  return <div role="button" tabIndex={0} aria-pressed={selected} aria-label={[isRichCard ? `[${option.value}]` : null, option.label, selected ? copy.choice.selectedAriaLabel : null].filter(Boolean).join(', ')} className={cn(AI_BUILDER_CHOICE_CHIP_CLASS_NAME, isRichCard ? 'items-start' : 'items-center')} onClick={onSelect} onKeyDown={event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelect();
  }} data-sentry-component="AiBuilderChoiceOptionShell" data-sentry-source-file="AiBuilderChoiceOptionShell.tsx">
      <span className="flex size-lg flex-none items-center justify-center">
        {indicator}
      </span>
      {option.emoji && <span aria-hidden="true" className="flex-none leading-none">
          {option.emoji}
        </span>}
      {isRichCard ? <span className="flex min-w-0 flex-col text-left">
          <span className="text-body-md-semibold text-default">
            {`[${option.value}]`}
          </span>
          <span className="text-body-sm-medium text-neutral">
            {option.label}
          </span>
        </span> : <span className="min-w-0 truncate text-left text-body-md-semibold text-default">
          {option.label}
        </span>}
    </div>;
};